import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box, Button, Typography, Icon } from '@material-ui/core';
import { GridOverlay, GridToolbarContainer } from '@material-ui/data-grid';
import { useDialog } from '../../../../common/hooks';
import { DataTable, EmptyState } from '../../../../common/components';
import { Device } from '../../../../types';

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    '& .MuiDataGrid-root': {
      border: '0',
    },
  },
  red: {
    color: theme.palette.error.main,
  },
}));

export type DeviceListProps = {
  data: Array<Device>;
  onDeleteDevices: (deviceIds: Array<string>) => void;
  actionsDisabled: boolean;
};

function DeviceList(props: DeviceListProps) {
  const [selectedDevices, setSelectedDevices] = useState<Array<string>>([]);
  const [t] = useTranslation();
  // @ts-expect-error
  const { showDialog } = useDialog();
  const classes = useStyles();

  const { data, onDeleteDevices, actionsDisabled } = props;

  const getColumnDefs = () => [
    {
      field: 'name',
      headerName: t('device.name'),
      width: 250,
      flex: 0.5,
    },
    {
      field: 'type',
      headerName: t('device.type'),
      width: 250,
      flex: 0.5,
    },
    {
      field: 'deviceClass',
      headerName: t('device.deviceClass'),
      width: 250,
      flex: 0.5,
    },
    {
      field: 'deviceId',
      headerName: t('device.deviceId'),
      width: 250,
      flex: 1,
    },
    {
      field: 'accessed',
      headerName: t('device.accessed'),
      width: 200,
    },
  ];

  const getRowData = () =>
    data.map(device => ({
      ...device,
      id: device.deviceId,
    }));

  const handleShowDeleteDevicesDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('device.devicesDeleteConfirm')}</Typography>,
      onConfirm: () => {
        const devices = data.filter(device => selectedDevices.includes(device.deviceId));
        onDeleteDevices(devices.map(device => device.deviceId));
      },
    });
  };

  const handleSelectDevices = (params: { selectionModel: Array<string> }) => {
    const { selectionModel } = params;
    setSelectedDevices(selectionModel);
  };

  const renderToolbar = () => (
    <GridToolbarContainer>
      <Button
        id="DeviceList-deleteDevices"
        variant="outlined"
        color="primary"
        startIcon={<Icon>delete</Icon>}
        onClick={handleShowDeleteDevicesDialog}
        disabled={actionsDisabled || _.isEmpty(selectedDevices)}
      >
        {t('device.devicesDelete')}
      </Button>
    </GridToolbarContainer>
  );

  const renderNoRowsOverlay = () => (
    <GridOverlay>
      <EmptyState variant="empty" message={t('device.devicesEmpty')} />
    </GridOverlay>
  );

  return (
    <Box id="DeviceList-container" className={classes.container}>
      <DataTable
        id="DeviceList-table"
        components={{
          Toolbar: renderToolbar,
          NoRowsOverlay: renderNoRowsOverlay,
        }}
        columns={getColumnDefs()}
        rows={getRowData()}
        selectionModel={selectedDevices}
        onSelectionModelChange={handleSelectDevices}
        pageSize={20}
        paginationMode="client"
        pagination
        checkboxSelection
      />
    </Box>
  );
}

export default DeviceList;
